import { CurrencySwitcherCompact } from "#components/currency-switcher-compact"
import { LocaleSwitcherCompact } from "#components/locale-switcher-compact"
import { PrivacyToggle } from "#components/privacy-toggle"
import { ThemeToggle } from "#components/theme-toggle"
import { SidebarInset, SidebarProvider, SidebarTrigger } from "#components/ui/sidebar"
import { Toaster } from "#components/ui/sonner"
import { useFeatureFlags } from "#hooks/use-feature-flags"
import { useUser } from "@/hooks/use-auth"
import { Separator } from "@radix-ui/react-separator"
import React, { lazy, Suspense } from "react"
import { I18nextProvider } from "react-i18next"
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom"
import { ThemeProvider, useTheme } from "./components/theme-provider"
import { AccountProvider } from "./contexts/AccountContext"
import { AuthProvider, useAuth } from "./contexts/AuthContext"
import { PrivacyProvider } from "./contexts/PrivacyContext"
import i18n from "./lib/i18n"

// Lazy load heavy components
const FloatingChat = lazy(() =>
  import("#components/chat/floating-chat").then((module) => ({ default: module.FloatingChat }))
)
const AppSidebar = lazy(() =>
  import("#components/sidebar/app-sidebar").then((module) => ({ default: module.AppSidebar }))
)

const CategoriesPage = lazy(() => import("./pages/Categories"))
const DashboardPage = lazy(() => import("./pages/Dashboard"))
const ExpensesPage = lazy(() => import("./pages/Expenses"))
const PartnersPage = lazy(() => import("./pages/Partners"))
const SettingsPage = lazy(() => import("./pages/Settings"))
const HelpPage = lazy(() => import("./pages/Help"))
const LoginPage = lazy(() => import("./pages/Login"))
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmail"))

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
)

const AppHeader: React.FC = () => {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"

  return (
    <header className="sticky top-0 z-50 relative flex h-16 shrink-0 items-center justify-between gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 px-2 sm:px-4 bg-background/95 backdrop-blur-sm overflow-hidden">
      {/* Gradient border bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background: isDark
            ? "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.1) 20%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.1) 80%, transparent 100%)"
            : "linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.08) 20%, rgba(0, 0, 0, 0.12) 50%, rgba(0, 0, 0, 0.08) 80%, transparent 100%)",
        }}
      />
      <div className="flex items-center gap-2 flex-shrink-0">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mr-2 data-[orientation=vertical]:h-4 hidden sm:block"
        />
      </div>
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 min-w-0">
        <LocaleSwitcherCompact />
        <CurrencySwitcherCompact />
        <PrivacyToggle />
        <ThemeToggle />
      </div>
    </header>
  )
}

const AuthenticatedLayout: React.FC = () => {
  const { data: featureFlags, isLoading: isLoadingFlags } = useFeatureFlags()
  const { data: user } = useUser()

  if (user && !user.emailConfirmedAt) {
    return <VerifyEmailPage />
  }

  return (
    <>
      <div className="flex">
        <SidebarProvider>
          <Suspense fallback={<div className="w-64" />}>
            <AppSidebar />
          </Suspense>
          <SidebarInset>
            <AppHeader />
            <div className="flex flex-1 flex-col gap-4 pt-0 flex-1 overflow-auto">
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/expenses" element={<ExpensesPage />} />
                  <Route path="/categories" element={<CategoriesPage />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/help/*" element={<HelpPage />} />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Suspense>
            </div>
          </SidebarInset>
        </SidebarProvider>
      </div>
      {!isLoadingFlags && featureFlags?.mcpEnabled && (
        <Suspense fallback={null}>
          <FloatingChat />
        </Suspense>
      )}
    </>
  )
}

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-secondary">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {!isAuthenticated ? (
        <Route path="*" element={<LoginPage />} />
      ) : (
        <Route path="*" element={<AuthenticatedLayout />} />
      )}
    </Routes>
  )
}

const App: React.FC = () => {
  return (
    <>
      <ThemeProvider defaultTheme="system" enableSystem disableTransitionOnChange>
        <PrivacyProvider>
          <AuthProvider>
            <AccountProvider>
              <I18nextProvider i18n={i18n}>
                <Router
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <AppContent />
                </Router>
                <Toaster />
              </I18nextProvider>
            </AccountProvider>
          </AuthProvider>
        </PrivacyProvider>
      </ThemeProvider>
    </>
  )
}

export default App
